import React, { useState, useEffect, useReducer } from 'react'
import { Lexicon } from '@nitidbit/lexicon'
import ClinicFinderForm from './ClinicFinderForm/ClinicFinderForm.jsx'
import ClinicFinderResults from './ClinicFinderResults.jsx'
import AddClinic from './AddClinic/AddClinic.jsx'
import FinderDisclaimer from './FinderDisclaimer/FinderDisclaimer.jsx'
import { updateSearchParams } from '../search/updateSearchParams.js'
import './ClinicFinder.scss'
import { NavigateClinicOffering } from '../../shared/constants.js'
import lexiconRaw from './ClinicFinderStrings.json'

const locale = document.documentElement.lang || 'en'
const lexicon = new Lexicon(
  lexiconRaw,
  locale,
  'bedsider/app/javascript/components/search/ClinicFinder/ClinicFinderStrings.json'
)
const finderLexicon = lexicon.subset('ClinicFinder')
const SEARCH_API_URL = '/api/clinics/v4'
const initialURLParams = new URLSearchParams(window.location.search)
const performSearchOnPageLoad = Array.from(initialURLParams.keys()).length > 0

const ClinicFinder = ({ googleMapsApiKey }) => {
  const [hasPerformedSearch, setHasPerformedSearch] = useState(
    performSearchOnPageLoad
  )
  const [navigateResponse, setNavigateResponse] = useState({})

  const urlWithOfferings = initialURLParams.get('with_offerings') || []

  const [searchTerms, updateSearchTerms] = useReducer(
    (oldState, stateUpdate) => ({ ...oldState, ...stateUpdate }),
    {
      location: initialURLParams.get('location') || '',
      filterImplant: urlWithOfferings.includes(
        NavigateClinicOffering.IMPLANT_AVAILABLE
      ),
      filterIUD: urlWithOfferings.includes(
        NavigateClinicOffering.IUD_AVAILABLE
      ),
    }
  )

  const fetchClinics = (searchParams) => {
    // limit the results to just 25 clinics
    searchParams.set('per_page', 25)
    // clinics with birth control for abortion patients only should not show on bedsider
    searchParams.set('exclude_bc_for_ab_patients_only', true)

    // if the old navigate response had an error, clear it
    if ('error' in navigateResponse) setNavigateResponse({})

    const paramsString = searchParams.toString()
    // console.log('paramsString',paramsString);
    fetch(`${SEARCH_API_URL}?${paramsString}`)
      .then((response) => response.json())
      .then((responseJson) => {
        setNavigateResponse(responseJson)
      })
      .catch((exception) => {
        // HTTP errors above, plus other network errors
        setNavigateResponse({ error: exception })
      })

    // setNavigateResponse(dummyClinicResponse)
  }

  const onSearch = () => {
    const newParams = { location: searchTerms.location }

    // could someday pass in these keys instead of hardcoding them
    const withOfferings = []
    if (searchTerms.filterImplant)
      withOfferings.push(NavigateClinicOffering.IMPLANT_AVAILABLE)
    if (searchTerms.filterIUD)
      withOfferings.push(NavigateClinicOffering.IUD_AVAILABLE)
    if (withOfferings.length > 0) {
      newParams['with_offerings'] = withOfferings.join(',')
    }

    const paramsString = updateSearchParams(newParams, false)
    setNavigateResponse({})
    fetchClinics(paramsString)
    setHasPerformedSearch(true)
  }

  // When user changes filters...
  useEffect(() => {
    if (hasPerformedSearch) {
      onSearch()
    }
  }, [searchTerms.filterImplant, searchTerms.filterIUD])

  // Search on pageload if we have the parameters for it
  useEffect(() => {
    if (performSearchOnPageLoad) {
      onSearch()
    }
  }, [])

  return (
    <div className="ClinicFinder">
      {!hasPerformedSearch ? (
        <>
          <div className="search-container">
            <ClinicFinderForm
              searchTerms={searchTerms}
              updateSearchTerms={updateSearchTerms}
              onSearch={onSearch}
              isCondensed={false}
              showTitle={true}
            />
          </div>

          <div className="add-clinic-container">
            <AddClinic lexicon={finderLexicon.subset('AddClinic')} />
          </div>
        </>
      ) : (
        <ClinicFinderResults
          lexicon={lexicon.subset('ClinicFinderResults')}
          searchTerms={searchTerms}
          updateSearchTerms={updateSearchTerms}
          navigateResponse={navigateResponse}
          onSearch={onSearch}
          googleMapsApiKey={googleMapsApiKey}
        />
      )}

      <FinderDisclaimer
        disclaimerText={finderLexicon.get('disclaimer')}
        additionalClasses={hasPerformedSearch ? 'full-width' : ''}
      />
    </div>
  )
}

export default ClinicFinder
